import type { TagVisibilityCallback } from './AttributeComponent';
import type { MatchResult } from './matchResult';

import { useLogFeatureDisplay } from '../../../../homepage/logging/DisplayLog';
import { Tag, Box } from '../../../../mads';
import { Check } from '../../../../mads/icons';
import { useI18n } from '../../../../shared/I18n';

import { useEffect } from 'react';
import { type LayoutChangeEvent, Platform, StyleSheet } from 'react-native';

export function SalaryAttributeComponent({
    salary,
    jobKey,
    isHighlightPositive = true,
    onTagHeightChanged,
    onTagVisibilityChanged,
    tagVisibilityId,
    tagVisibilityOrder = 0,
}: {
    salary: string;
    jobKey: string;
    isHighlightPositive?: boolean;
    onTagHeightChanged?: (height: number) => void;
    onTagVisibilityChanged?: TagVisibilityCallback;
    tagVisibilityId?: string;
    tagVisibilityOrder?: number;
}) {
    const i18n = useI18n();
    const logFeatureDisplay = useLogFeatureDisplay();

    useEffect(() => {
        logFeatureDisplay({
            jobKey: jobKey,
            field: 'salaryYN',
            value: !!salary,
        });

        logFeatureDisplay({
            jobKey: jobKey,
            field: 'salary',
            value: salary,
        });

        if (!salary) {
            logFeatureDisplay({
                jobKey: jobKey,
                field: 'payNotProvidedYN',
                value: true,
            });
        }

        if (salary) {
            // Capture whether the salary chip rendered with the positive-match highlight.
            const salaryMatchResult: MatchResult =
                isHighlightPositive ? 'POSITIVE' : 'NEGATIVE';
            logFeatureDisplay({
                jobKey: jobKey,
                field: 'salaryMatchResult',
                value: salaryMatchResult,
            });
        }
    }, [jobKey, salary, isHighlightPositive, logFeatureDisplay]);

    const label = salary ? salary : i18n.gettext('Pay information not provided');

    useEffect(() => {
        return () => {
            if (tagVisibilityId) {
                onTagVisibilityChanged?.(tagVisibilityId, label, tagVisibilityOrder, false);
            }
        };
    }, [label, onTagVisibilityChanged, tagVisibilityId, tagVisibilityOrder]);

    return (
        <Box
            sx={styles.tagContainer}
            {...Platform.select({
                ios: { accessible: true },
                android: { screenReaderFocusable: true },
            })}
            onLayout={(event: LayoutChangeEvent) => {
                if (onTagHeightChanged) {
                    onTagHeightChanged(event.nativeEvent.layout.height);
                }
                if (tagVisibilityId) {
                    onTagVisibilityChanged?.(tagVisibilityId, label, tagVisibilityOrder, true);
                }
            }}
        >
            <Tag
                sx={styles.tag}
                status={isHighlightPositive ? 'success' : 'neutral'}
                icon={isHighlightPositive ? <Check size="xs" aria-hidden={true} /> : undefined}
            >
                {label}
            </Tag>
        </Box>
    );
}
const styles = StyleSheet.create({
    tagContainer: {
        marginLeft: 1,
        marginTop: 1,
        flexShrink: 1,
        maxWidth: '100%',
    },
    tag: {
        paddingHorizontal: 8,
        paddingVertical: 2,
        minHeight: 24,
    },
});
